import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { useAuth } from '../../auth/AuthProvider';
import { errMessage, secondaryBtnStyle } from '../../components/UI/ink';
import { integrationsApi } from '../../api/integrations';
import TelegramDrawer from '../../components/Settings/TelegramDrawer';
import ModelDrawer from '../../components/Settings/ModelDrawer';
import { ROADMAP, ROADMAP_CATS, ago } from '../../components/Settings/model';
import { Notice, SectionHead, StatusChip, kicker, panel } from '../../components/Settings/ui';

const MONO = { telegram: 'TG', ollama: 'OL', claude: 'CL', openrouter: 'OR' };

// One card per integration DragonEye actually talks to, with whether it's
// working; then the roadmap of what isn't built yet.
function statusOf(it) {
  if (!it.connected) return [null, 'NOT SET UP'];
  if (it.off_by_policy) return [null, 'OFF BY POLICY'];
  if (it.id === 'telegram' && !it.enabled) return [null, 'PAUSED'];
  if (it.health && !it.health.ok) return [false, 'FAILING'];
  if (it.id === 'telegram' && it.failed_24h > 0) return [false, 'FAILURES'];
  return [true, 'CONNECTED'];
}

function line2(it) {
  if (it.id === 'telegram') {
    if (!it.connected) return 'Not connected';
    return `${it.last_sent ? `Last message ${ago(it.last_sent)}` : 'Nothing sent yet'} · ${it.failed_24h || 0} failed in 24 h`;
  }
  if (it.id === 'openrouter') return it.off_by_policy ? 'External models are off in AI & automation' : 'Key set in the worker environment';
  if (it.off_by_policy) return 'External models are off in AI & automation';
  if (!it.health) return it.scope === 'platform' ? 'No run recorded yet' : it.connected ? 'Not tested yet' : '';
  return `${it.health.ok ? '' : 'Failed: '}${it.health.detail} · ${ago(it.health.checked_at)}`;
}

export default function IntegrationsSettings({ onGo }) {
  const { permissions = [] } = useAuth();
  const canEdit = permissions.includes('system:update');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [rm, setRm] = useState('next');
  const [drawer, setDrawer] = useState(null);

  const load = useCallback(() => integrationsApi.overview().then(d => { setData(d); setError(null); }).catch(err => setError(errMessage(err, 'Could not load integrations.'))), []);
  useEffect(() => { load(); }, [load]);

  const needle = q.trim().toLowerCase();
  const conn = (data?.integrations || []).filter(it => !needle || `${it.name} ${it.category} ${it.what}`.toLowerCase().includes(needle));
  const roadmap = useMemo(() => ROADMAP
    .filter(r => (rm === 'all' || (rm === 'next' ? r.next : r.cat === rm)) && (!needle || `${r.name} ${r.what}`.toLowerCase().includes(needle)))
    .sort((a, b) => Number(b.next) - Number(a.next)), [rm, needle]);
  const connectedN = (data?.integrations || []).filter(it => it.connected).length;

  const open = (it) => {
    if (it.id === 'telegram') setDrawer('telegram');
    else if (it.id === 'ollama' || it.id === 'claude') setDrawer(it.id);
    else if (it.id === 'openrouter') onGo?.('ai');
  };
  const action = (it) => (it.id === 'telegram' || it.id === 'ollama' || it.id === 'claude' ? (it.connected ? 'Configure' : 'Connect') : it.id === 'openrouter' ? 'AI policy' : null);

  return (
    <div className="flex flex-col gap-[18px]">
      <SectionHead
        title="Integrations"
        sub={data ? `Everything DragonEye talks to. ${connectedN} connected, ${ROADMAP.length} on the roadmap.` : 'Loading…'}
        right={
          <div className="flex-[0_1_300px] min-w-[200px] h-10 box-border flex items-center gap-2 px-3 rounded-[10px] border" style={panel}>
            <Search className="w-[15px] h-[15px]" strokeWidth={1.8} style={{ color: 'var(--ink-faint)' }} />
            <input type="search" aria-label="Search integrations" placeholder="Search integrations" value={q} onChange={e => setQ(e.target.value)} className="flex-grow min-w-0 h-[38px] bg-transparent border-0 outline-none text-[13.5px]" style={{ color: 'var(--ink-text)' }} />
          </div>
        }
      />
      {error && <Notice>{error}</Notice>}
      {data && !data.secrets_key_set && canEdit && (
        <Notice kind="warn">Stored keys are encrypted with a key derived from JWT_SECRET. Set a separate SECRETS_KEY secret on the Worker so rotating JWT_SECRET doesn’t lock you out of them.</Notice>
      )}

      <h3 className={`mt-1 mb-0 ${kicker}`} style={{ color: 'var(--ink-faint)' }}>IN USE · {conn.length}</h3>
      <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(270px,1fr))]">
        {conn.map(it => {
          const [ok, st] = statusOf(it);
          const btn = canEdit || it.id === 'openrouter' ? action(it) : null;
          const l2 = line2(it);
          return (
            <section key={it.id} className="flex flex-col gap-2.5 p-4 rounded-[14px] border" style={panel}>
              <span className="flex items-center gap-2.5">
                <span aria-hidden="true" className="w-[38px] h-[38px] shrink-0 flex items-center justify-center rounded-[10px] border font-mono text-xs font-medium" style={{ background: 'var(--surf-raised)', borderColor: 'var(--surf-line)' }}>{MONO[it.id]}</span>
                <span className="flex-grow min-w-0 flex flex-col gap-0.5"><span className="text-[15px] font-semibold">{it.name}</span><span className="font-mono text-[10px] tracking-[0.08em]" style={{ color: 'var(--ink-faint)' }}>{it.category}</span></span>
                <StatusChip ok={ok} text={st} />
              </span>
              <span className="text-[12.5px] leading-normal" style={{ color: 'var(--ink-muted)' }}>{it.what}</span>
              <span className="flex flex-col gap-[3px] py-2 border-y" style={{ borderColor: 'var(--surf-line)' }}>
                <span className="font-mono text-[11.5px] truncate" title={it.line1 || ''}>{it.line1 || '—'}</span>
                <span className="text-xs" style={{ color: it.health && !it.health.ok ? 'var(--sev-crit-fg)' : 'var(--ink-muted)' }}>{l2 || ' '}</span>
              </span>
              <span className="flex items-center justify-between gap-2 mt-auto">
                <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>{it.scope === 'platform' ? 'Platform-wide · set on the Worker' : 'This organisation'}</span>
                {btn && <button type="button" onClick={() => open(it)} className="h-9 px-3 rounded-lg border text-[13px]" style={secondaryBtnStyle}>{btn}</button>}
              </span>
            </section>
          );
        })}
      </div>

      <div className="flex flex-col gap-3 pt-2.5">
        <span className="flex flex-wrap items-baseline justify-between gap-2"><h3 className={`m-0 ${kicker}`} style={{ color: 'var(--ink-faint)' }}>ON THE ROADMAP · {ROADMAP.length}</h3><span className="text-[12.5px]" style={{ color: 'var(--ink-faint)' }}>Not built yet. Next up is what we plan to build first.</span></span>
        <div role="group" aria-label="Roadmap category" className="flex flex-wrap gap-1.5">
          {[['next', 'Next up'], ...Object.entries(ROADMAP_CATS), ['all', 'All']].map(([id, t]) => {
            const n = id === 'all' ? ROADMAP.length : id === 'next' ? ROADMAP.filter(r => r.next).length : ROADMAP.filter(r => r.cat === id).length;
            const on = rm === id;
            return <button key={id} type="button" aria-pressed={on} onClick={() => setRm(id)} className="h-9 flex items-center gap-[7px] px-3 rounded-[9px] border text-[13px]" style={{ background: on ? 'var(--surf-raised)' : 'var(--surf-surface)', borderColor: on ? 'var(--accent)' : 'var(--surf-line)' }}>{t}<span className="font-mono text-[11px]" style={{ color: 'var(--ink-faint)' }}>{n}</span></button>;
          })}
        </div>
        <div className="grid gap-2.5 grid-cols-[repeat(auto-fill,minmax(250px,1fr))]">
          {roadmap.map(r => (
            <div key={r.name} className="flex flex-col gap-2 p-3.5 rounded-xl border border-dashed" style={{ background: 'var(--surf-surface)', borderColor: 'var(--surf-line-strong)' }}>
              <span className="flex items-center gap-2.5">
                <span aria-hidden="true" className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg border font-mono text-[11px]" style={{ borderColor: 'var(--surf-line)', color: 'var(--ink-muted)' }}>{r.mono}</span>
                <span className="flex-grow min-w-0 flex flex-col gap-px"><span className="text-sm font-semibold">{r.name}</span><span className="font-mono text-[10px] tracking-[0.06em]" style={{ color: 'var(--ink-faint)' }}>{ROADMAP_CATS[r.cat].toUpperCase()}</span></span>
                <span className="h-5 inline-flex items-center px-1.5 rounded-[5px] border font-mono text-[9.5px] tracking-[0.06em]" style={{ borderColor: r.next ? 'var(--ink-text)' : 'var(--surf-line)', color: r.next ? 'var(--ink-text)' : 'var(--ink-faint)' }}>{r.next ? 'NEXT' : 'LATER'}</span>
              </span>
              <span className="text-[12.5px] leading-normal" style={{ color: 'var(--ink-muted)' }}>{r.what}</span>
              <span className="font-mono text-[10.5px]" style={{ color: 'var(--ink-faint)' }}>{r.kind.toUpperCase()}</span>
            </div>
          ))}
          {!roadmap.length && <span className="text-[13px]" style={{ color: 'var(--ink-muted)' }}>Nothing on the roadmap matches.</span>}
        </div>
      </div>

      <TelegramDrawer open={drawer === 'telegram'} onClose={() => setDrawer(null)} onChanged={load} />
      <ModelDrawer provider={drawer === 'ollama' || drawer === 'claude' ? drawer : null} onClose={() => setDrawer(null)} onChanged={load} />
    </div>
  );
}
